import { campaignCopy } from '@discovr/features';
import type { Post, PostHoldEvent } from '@discovr/types';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { statusLabel } from './parts/statusMap';
import { heldAtLabel, holdDateLabel, isPostHeld } from './ConfirmedTab.hold';
import { holdReasonLabel } from './ConfirmedTab.holdReasons';

const copy = campaignCopy.creator.hold;
const ACTION_LABEL: Record<PostHoldEvent['action'], string> = { held: copy.details.held, released: copy.details.released };

function HoldHistory({ events }: { events: PostHoldEvent[] }) {
  if (events.length === 0) return null;
  // The backend appends events, so reversing gives newest first.
  const newestFirst = [...events].reverse();
  return (
    <div className="flex flex-col gap-1.5 border-t border-border pt-2">
      <p className="text-xs font-medium text-muted-foreground">{copy.details.history}</p>
      <ol aria-label={copy.details.historyLabel} className="flex flex-col gap-2">
        {newestFirst.map((event, i) => (
          <li key={`${event.at}-${i}`} className="flex flex-col gap-0.5 text-xs">
            <span className="font-medium text-foreground">
              {ACTION_LABEL[event.action] ?? event.action} · {holdReasonLabel(event.reason_code)}
            </span>
            <span className="text-muted-foreground">{holdDateLabel(event.at)}</span>
            {event.note && <span className="whitespace-pre-wrap wrap-break-word text-foreground">{event.note}</span>}
          </li>
        ))}
      </ol>
    </div>
  );
}

export function HoldStatusLine({ post }: { post: Post }) {
  if (!isPostHeld(post) || !post.hold) return null;
  const { reason_code, note, history } = post.hold;
  const heldAt = heldAtLabel(post);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-amber-100 px-3 py-[3px] text-[11px] font-medium uppercase text-(--foreground-warning-default) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="size-1.5 shrink-0 rounded-full bg-(--foreground-warning-default)" />
          {copy.badge}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" role="dialog" aria-label={copy.details.label} className="flex w-72 flex-col gap-2 text-sm">
        <p className="font-medium text-foreground">{holdReasonLabel(reason_code)}</p>
        {note && <p className="whitespace-pre-wrap wrap-break-word text-foreground">{note}</p>}
        <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
          {heldAt && <span>{`${copy.details.heldAt} ${heldAt}`}</span>}
          <span>{`${copy.details.returnsTo} ${statusLabel(post.status)}`}</span>
        </div>
        <HoldHistory events={history ?? []} />
      </PopoverContent>
    </Popover>
  );
}
